<template>
  <div>
    <div v-if="showOverlay" class="overlay"></div>
    <div v-if="showTour" class="tour">
      <div v-if="currentStep" ref="tooltip" class="tooltip-tour" 
           :class="[tooltipPosition.position, { 'dragging': isDragging }]" 
           :style="{
             top: tooltipPosition.top + 'px',
             left: tooltipPosition.left + 'px',
           }"
           @mousedown="startDragging"
           @touchstart="startDragging">
        
        <!-- Indicador de arrastre -->
        <div class="drag-handle">
          <i class="fas fa-grip-dots-vertical"></i>
        </div>
        
        <div class="tooltip-tour-content">
          <div class="content-header">
            <h4 class="tour-title">
              <b>{{ currentStep.title }}</b>
            </h4>
            <i class="close-button fas fa-xmark" @click.prevent="endTour"></i>
          </div>
          
          <div class="content-body">
            <p class="tour-description">
              {{ currentStep.description }}
            </p>
          </div>
          
          <!-- BOTÓN PARA CONTINUAR CON EL TOUR EN OTRA VISTA -->
          <vs-button @click.prevent="nextTour(validSteps[currentStepIndex].redireccionarOtroPasoAPaso)" success flat
            v-if="validSteps[currentStepIndex].redireccionarOtroPasoAPaso" class="continue-button">
            Continuar Tour <i class="fa-solid fa-arrow-right ml-2"></i>
          </vs-button>
          
          <div class="progress-tour">
            <span v-for="(step, index) in validSteps" :key="index" class="step-dot" :class="{
              completed: index < currentStepIndex,
              active: index === currentStepIndex,
            }" @click.prevent="goToStep(index)"></span>
          </div>
          
          <div class="tour-footer">
            <span class="step-counter">{{ currentStepIndex + 1 }} / {{ validSteps.length }}</span>
            
            <div class="navigation-buttons">
              <button @click.prevent="prevStep" class="nav-button prev-button" 
                v-if="currentStepIndex > 0">
                <i class="fas fa-chevron-left"></i>
              </button>
              
              <button @click.prevent="nextStep" class="nav-button next-button"
                v-if="currentStepIndex < validSteps.length - 1">
                <i class="fas fa-chevron-right"></i>
              </button>
              
              <button @click.prevent="endTour" class="nav-button end-button"
                v-if="currentStepIndex === validSteps.length - 1 && !validSteps[currentStepIndex].redireccionarOtroPasoAPaso">
                <i class="fas fa-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      scrollTimeout: null,
      currentStepIndex: 0,
      steps: [],
      validSteps: [],
      showOverlay: false,
      showTour: false,
      currentStepPosition: { top: 0, left: 0 },
      tooltipPosition: { top: 0, left: 0, position: 'bottom' },
      
      // Variables para el arrastre
      isDragging: false,
      dragStartPos: { x: 0, y: 0 },
      tooltipStartPos: { top: 0, left: 0 },
      hasBeenDragged: false,
    };
  },
  props: {
    datos: {
      type: String,
    }
  },
  mounted() {
    console.log(this.datos);

    window.addEventListener("resize", this.handleResize);
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("keydown", this.handleGlobalKey);
    
    // Eventos para el arrastre
    document.addEventListener("mousemove", this.handleDrag);
    document.addEventListener("mouseup", this.stopDragging);
    document.addEventListener("touchmove", this.handleDrag, { passive: false });
    document.addEventListener("touchend", this.stopDragging);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("keydown", this.handleGlobalKey);
    
    // Limpiar eventos de arrastre
    document.removeEventListener("mousemove", this.handleDrag);
    document.removeEventListener("mouseup", this.stopDragging);
    document.removeEventListener("touchmove", this.handleDrag);
    document.removeEventListener("touchend", this.stopDragging);
  },
  methods: {
    // Métodos de arrastre
    startDragging(event) {
      // Prevenir el arrastre si se hace clic en botones o elementos interactivos
      if (event.target.closest('.nav-button, .close-button, .step-dot, .continue-button')) {
        return;
      }
      
      this.isDragging = true;
      
      // Obtener posición inicial del cursor/touch
      const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
      const clientY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;
      
      this.dragStartPos = { x: clientX, y: clientY };
      this.tooltipStartPos = { 
        top: this.tooltipPosition.top, 
        left: this.tooltipPosition.left 
      };
      
      // Deshabilitar transiciones durante el arrastre
      this.disableTransitions();
      
      // Prevenir selección de texto
      event.preventDefault();
    },
    
    handleDrag(event) {
      if (!this.isDragging) return;
      
      event.preventDefault();
      
      // Obtener posición actual del cursor/touch
      const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX;
      const clientY = event.type === 'touchmove' ? event.touches[0].clientY : event.clientY;
      
      // Calcular el desplazamiento
      const deltaX = clientX - this.dragStartPos.x;
      const deltaY = clientY - this.dragStartPos.y;
      
      // Calcular nueva posición
      let newLeft = this.tooltipStartPos.left + deltaX;
      let newTop = this.tooltipStartPos.top + deltaY;
      
      // Obtener dimensiones del tooltip y viewport
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;
      
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Limitar la posición para que no se salga de la pantalla
      const margin = 10;
      newLeft = Math.max(margin, Math.min(viewportWidth - tooltipRect.width - margin, newLeft));
      newTop = Math.max(margin, Math.min(viewportHeight - tooltipRect.height - margin, newTop));
      
      // Actualizar posición
      this.tooltipPosition.left = newLeft;
      this.tooltipPosition.top = newTop;
      
      this.hasBeenDragged = true;
    },
    
    stopDragging() {
      if (!this.isDragging) return;
      
      this.isDragging = false;
      
      // Reactivar transiciones
      this.enableTransitions();
    },
    
    disableTransitions() {
      const tooltip = this.$refs.tooltip;
      if (tooltip) {
        tooltip.style.transition = 'none';
      }
    },
    
    enableTransitions() {
      const tooltip = this.$refs.tooltip;
      if (tooltip) {
        // Usar setTimeout para asegurar que se aplique después del frame actual
        setTimeout(() => {
          tooltip.style.transition = '';
        }, 10);
      }
    },
    
    // Métodos existentes del tour
    handleGlobalKey(event) {
      if (event.key === "Enter") {
        this.nextStep();
      } else if (event.key === "Escape") {
        this.endTour();
      }
    },

    handleResize() {
      // Solo reposicionar automáticamente si no ha sido arrastrado manualmente
      if (!this.hasBeenDragged) {
        this.updateTooltipPosition();
      } else {
        // Asegurar que sigue dentro de los límites después del resize
        this.constrainToViewport();
      }
    },

    handleScroll() {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        // Solo reposicionar automáticamente si no ha sido arrastrado manualmente
        if (!this.hasBeenDragged) {
          this.updateTooltipPosition();
        }
      }, 50);
    },
    
    constrainToViewport() {
      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;
      
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const margin = 10;
      let newLeft = this.tooltipPosition.left;
      let newTop = this.tooltipPosition.top;
      
      newLeft = Math.max(margin, Math.min(viewportWidth - tooltipRect.width - margin, newLeft));
      newTop = Math.max(margin, Math.min(viewportHeight - tooltipRect.height - margin, newTop));
      
      this.tooltipPosition.left = newLeft;
      this.tooltipPosition.top = newTop;
    },

    validateSteps(steps) {
      const validatedSteps = [];
      const skippedSteps = [];

      steps.forEach((step, index) => {
        if (step.elementId) {
          const element = document.getElementById(step.elementId);
          if (element) {
            validatedSteps.push({
              ...step,
              originalIndex: index 
            });
          } else {
            skippedSteps.push({
              ...step,
              originalIndex: index,
              reason: 'Elemento no encontrado'
            });
          }
        } else {
          validatedSteps.push({
            ...step,
            originalIndex: index
          });
        }
      });

      if (skippedSteps.length > 0) {
        console.info(`Tour: Se omitieron ${skippedSteps.length} paso(s) porque sus elementos no existen:`, skippedSteps);
      }

      console.info(`Tour: ${validatedSteps.length} de ${steps.length} pasos son válidos`);
      
      return validatedSteps;
    },

    revalidateCurrentStep() {
      const currentStep = this.validSteps[this.currentStepIndex];
      if (currentStep && currentStep.elementId) {
        const element = document.getElementById(currentStep.elementId);
        if (!element) {
          console.warn(`Tour: El elemento "${currentStep.elementId}" ya no existe. Saltando al siguiente paso.`);
          this.nextStep();
          return false;
        }
      }
      return true;
    },

    goToStep(index) {
      if (index >= 0 && index < this.validSteps.length) {
        this.removeHighlight();
        this.currentStepIndex = index;
        
        if (this.revalidateCurrentStep()) {
          this.focusElement();
        }
      }
    },

    startTour(steps) {
      this.steps = steps;
      this.validSteps = this.validateSteps(steps);
      
      if (this.validSteps.length === 0) {
        console.error('Tour: No hay pasos válidos para mostrar. Todos los elementos especificados no existen en el DOM.');
        this.$emit('tourError', 'No hay elementos válidos para el tour');
        return;
      }

      this.currentStepIndex = 0;
      this.showOverlay = true;
      this.showTour = true;
      this.hasBeenDragged = false; // Reset del estado de arrastre
      document.documentElement.classList.add("no-scroll");
      
      this.$nextTick(() => {
        this.focusElement();
      });
      
      let idActual = this.validSteps[0].elementId;
      this.$emit("idActual", idActual);
      this.$emit('tourStarted', {
        totalSteps: this.steps.length,
        validSteps: this.validSteps.length,
        skippedSteps: this.steps.length - this.validSteps.length
      });
    },

    nextStep() {
      if (this.currentStepIndex < this.validSteps.length - 1) {
        this.removeHighlight();
        this.hasBeenDragged = false; // Reset para el siguiente paso

        let idActual = this.validSteps[this.currentStepIndex].elementId;
        this.currentStepIndex++;

        if (this.revalidateCurrentStep()) {
          this.$emit("idActual", idActual);
          this.focusElement();
        }
      }
    },

    prevStep() {
      if (this.currentStepIndex > 0) {
        this.removeHighlight();
        this.hasBeenDragged = false; // Reset para el paso anterior
        this.currentStepIndex--;
        
        if (this.revalidateCurrentStep()) {
          this.focusElement();
        }
      }
    },

    endTour() {
      this.removeHighlight();
      this.currentStepIndex = 0;
      this.steps = [];
      this.validSteps = [];
      this.showOverlay = false;
      this.showTour = false;
      this.hasBeenDragged = false;
      document.documentElement.classList.remove("no-scroll");
      this.$emit('tourEnded');
    },

    updateTooltipPosition() {
      const step = this.validSteps[this.currentStepIndex];
      if (!step || !step.elementId) return;

      const element = document.getElementById(step.elementId);
      if (!element) {
        console.warn(`Tour: Elemento "${step.elementId}" no encontrado durante actualización de posición`);
        this.nextStep();
        return;
      }

      const tooltip = this.$refs.tooltip;
      if (!tooltip) return;

      const elementRect = element.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const positions = this.calculatePositions(elementRect, tooltipRect, viewportWidth, viewportHeight);
      const bestPosition = this.getBestPosition(positions);

      this.tooltipPosition = {
        top: bestPosition.top,
        left: bestPosition.left,
        position: bestPosition.position
      };
    },

    calculatePositions(elementRect, tooltipRect, viewportWidth, viewportHeight) {
      const margin = 15;
      const arrowOffset = 10;

      const centerX = elementRect.left + elementRect.width / 2 - tooltipRect.width / 2;
      const centerY = elementRect.top + elementRect.height / 2 - tooltipRect.height / 2;

      return {
        bottom: {
          position: 'bottom',
          top: elementRect.bottom + margin + arrowOffset,
          left: centerX,
          score: this.calculatePositionScore(
            centerX, elementRect.bottom + margin + arrowOffset,
            tooltipRect.width, tooltipRect.height,
            viewportWidth, viewportHeight
          )
        },
        top: {
          position: 'top',
          top: elementRect.top - tooltipRect.height - margin - arrowOffset,
          left: centerX,
          score: this.calculatePositionScore(
            centerX, elementRect.top - tooltipRect.height - margin - arrowOffset,
            tooltipRect.width, tooltipRect.height,
            viewportWidth, viewportHeight
          )
        },
        right: {
          position: 'right',
          top: centerY,
          left: elementRect.right + margin + arrowOffset,
          score: this.calculatePositionScore(
            elementRect.right + margin + arrowOffset, centerY,
            tooltipRect.width, tooltipRect.height,
            viewportWidth, viewportHeight
          )
        },
        left: {
          position: 'left',
          top: centerY,
          left: elementRect.left - tooltipRect.width - margin - arrowOffset,
          score: this.calculatePositionScore(
            elementRect.left - tooltipRect.width - margin - arrowOffset, centerY,
            tooltipRect.width, tooltipRect.height,
            viewportWidth, viewportHeight
          )
        }
      };
    },

    calculatePositionScore(left, top, width, height, viewportWidth, viewportHeight) {
      const horizontalOverflow = Math.max(0,
        Math.max(0, left + width - viewportWidth) +
        Math.max(0, 0 - left)
      );

      const verticalOverflow = Math.max(0,
        Math.max(0, top + height - viewportHeight) +
        Math.max(0, 0 - top)
      );

      const visibleWidth = Math.max(0, width - horizontalOverflow);
      const visibleHeight = Math.max(0, height - verticalOverflow);
      const visibleArea = (visibleWidth * visibleHeight) / (width * height);

      return visibleArea * 100;
    },

    getBestPosition(positions) {
      let bestPosition = positions.bottom;
      let bestScore = positions.bottom.score;

      if (positions.top.score > bestScore) {
        bestPosition = positions.top;
        bestScore = positions.top.score;
      }

      if (positions.right.score > bestScore) {
        bestPosition = positions.right;
        bestScore = positions.right.score;
      }

      if (positions.left.score > bestScore) {
        bestPosition = positions.left;
        bestScore = positions.left.score;
      }

      bestPosition.left = Math.max(10, Math.min(window.innerWidth - 10 - this.$refs.tooltip.offsetWidth, bestPosition.left));
      bestPosition.top = Math.max(10, Math.min(window.innerHeight - 10 - this.$refs.tooltip.offsetHeight, bestPosition.top));

      return bestPosition;
    },

    focusElement() {
      const step = this.validSteps[this.currentStepIndex];
      if (!step || !step.elementId) return;

      const element = document.getElementById(step.elementId);
      if (!element) {
        console.warn(`Tour: Elemento "${step.elementId}" no encontrado durante enfoque`);
        this.nextStep();
        return;
      }

      if (!this.isElementInViewport(element)) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });

        setTimeout(() => {
          this.updateTooltipPosition();
        }, 500);
      } else {
        this.updateTooltipPosition();
      }

      element.classList.add("highlight");
    },

    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    },

    removeHighlight() {
      const step = this.validSteps[this.currentStepIndex];
      if (step && step.elementId) {
        const element = document.getElementById(step.elementId);
        if (element) {
          element.classList.remove("highlight");
        }
      }
    },

    nextTour(redireccion) {
      this.removeHighlight();
      this.currentStepIndex = 0;
      this.steps = [];
      this.validSteps = [];
      this.showOverlay = false;
      this.showTour = false;
      this.hasBeenDragged = false;
      document.documentElement.classList.remove("no-scroll");

      console.log("La redirección es ", redireccion);
      this.$router.push({
        path: redireccion,
        query: { habilitarTour: true }
      });
    }
  },
  computed: {
    currentStep() {
      return this.validSteps[this.currentStepIndex];
    },
  },
};
</script>

<style>
.overlay {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.6);
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999 !important;
  backdrop-filter: blur(2px);
}

.tour {
  position: relative;
  z-index: 1005 !important;
}

.tooltip-tour {
  position: fixed;
  background-color: var(--lego-bg, #102444);
  padding: 16px 20px;
  border-radius: 12px;
  min-width: 290px;
  max-width: 320px;
  min-height: 160px;
  z-index: 1001 !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  user-select: none;
  cursor: grab;
}

/* Flechas para cada posición */
.tooltip-tour:after {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
  border: 10px solid transparent;
  transition: opacity 0.3s ease;
}

.tooltip-tour.dragging:after {
  opacity: 0.3;
}

.tooltip-tour.bottom:after {
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-bottom: 10px solid var(--lego-bg, #102444);
}

.tooltip-tour.top:after {
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-top: 10px solid var(--lego-bg, #102444);
}

.tooltip-tour.left:after {
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
  border-left: 10px solid var(--lego-bg, #102444);
}

.tooltip-tour.right:after {
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
  border-right: 10px solid var(--lego-bg, #102444);
}

.tooltip-tour-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 16px;
  text-align: left !important;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.tour-title {
  font-size: 1.1rem;
  margin: 0;
  flex: 1;
  padding-right: 10px;
}

.content-body {
  flex: 1;
  margin-bottom: 16px;
}

.tour-description {
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.continue-button {
  margin-bottom: 16px;
}

.progress-tour {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 12px;
  padding: 8px 0;
}

.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.step-dot.completed {
  background-color: #ccc;
}

.step-dot.active {
  background-color: #df122e;
  transform: scale(1.2);
}

.step-dot:not(.completed):not(.active) {
  background-color: rgba(223, 18, 46, 0.5);
}

.step-dot:hover {
  transform: scale(1.2);
}

.tour-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.step-counter {
  font-size: 0.9rem;
  font-weight: 500;
}

.navigation-buttons {
  display: flex;
  gap: 8px;
}

.nav-button {
  background-color: var(--lego-bg, #102444);
  border: 1px solid var(--lego-icon, white);
  color: var(--lego-icon, white);
  font-size: 14px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.25s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.nav-button:hover {
  transform: scale(1.1);
}

.next-button:hover,
.prev-button:hover {
  border-color: #df122e;
}

.end-button:hover {
  border-color: #16a275;
}

.close-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  padding: 4px;
}

.close-button:hover {
  opacity: 1;
}

/* Resaltado del elemento activo */
.highlight {
  position: relative;
  z-index: 1001 !important;
  animation: pulse 2s infinite;
  box-shadow: 0 0 0 4px rgba(223, 18, 46, 0.5);
  border-radius: 4px;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(223, 18, 46, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(223, 18, 46, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(223, 18, 46, 0);
  }
}

.no-scroll {
  overflow: hidden;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .tooltip-tour {
    min-width: 260px;
    max-width: calc(100vw - 20px);
    padding: 12px 16px;
  }
  
  .tour-title {
    font-size: 1rem;
  }
  
  .tour-description {
    font-size: 0.9rem;
  }
  
  .nav-button {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
  
  .step-counter {
    font-size: 0.8rem;
  }
  
  .drag-handle {
    font-size: 10px;
  }
}



</style>